import { inject } from 'vue';
import { useMessageBox } from './useMessageBox';

export interface MessageOptions {
  msgStr: string;
  durationMs?: number;
  showClose?: boolean;
  useHTML?: boolean;
  icon?: boolean;
  onClose?: () => void;
  type?: 'success' | 'error' | 'warning' | 'info' | 'loading';
}
export type MessageType = (
  value:
    | string
    | {
        type?: string;
        message: string;
        duration?: number;
        showClose?: boolean;
        iconClass?: string;
        readonly onClose?: () => void;
        useHTML?: boolean;
      },
) => Record<string, () => void>;

export const useMessage = () => {
  const message = inject<MessageType>('message');
  if (!message) {
    throw new Error('useMessage must be used within a MessageBoxProvider');
  }

  const showMsg = ({
    type,
    msgStr,
    durationMs = 3000,
    showClose = false,
    useHTML = false,
    icon = false,
    onClose,
  }: MessageOptions) => {
    const iconClass = {
      success: 'icon-fill-check-circle',
      error: 'icon-fill-error-circle',
      warning: 'icon-fill-warning',
      info: 'icon-info-circle',
      loading: 'icon-refresh',
    }[type ?? 'info'];
    const messageType = type === 'loading' ? 'info' : type;

    return message({
      message: msgStr,
      type: messageType,
      duration: durationMs,
      showClose,
      useHTML,
      iconClass: icon ? iconClass : '',
      onClose,
    });
  };

  const showSuccessMsg = (options: MessageOptions) => showMsg({ ...options, type: 'success' });
  const showWarningMsg = (options: MessageOptions) => showMsg({ ...options, type: 'warning' });
  const showErrorMsg = (options: MessageOptions) => showMsg({ ...options, type: 'error' });
  const showInfoMsg = (options: MessageOptions) => showMsg({ ...options, type: 'info' });

  return {
    showMsg,
    showSuccessMsg,
    showWarningMsg,
    showErrorMsg,
    showInfoMsg,
    ...useMessageBox(),
  };
};
